import { useId, useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface AccordionItemProps {
  title: string;
  /** One line under the title, shown whether open or closed. */
  description?: string;
  /** Short summary on the right of the header, e.g. a count. */
  badge?: string;
  defaultOpen?: boolean;
  children: ReactNode;
  className?: string;
}

/**
 * One collapsible section.
 *
 * Each item owns its own open state, so several can be open at once. That
 * suits a reference table — comparing two groups should not mean losing the
 * first one — where a single-open accordion would fight the reader.
 *
 * The panel is unmounted when closed rather than hidden, so a long table costs
 * nothing to have on the page.
 */
export function AccordionItem({
  title,
  description,
  badge,
  defaultOpen = false,
  children,
  className,
}: AccordionItemProps) {
  const [isOpen, setOpen] = useState(defaultOpen);
  const panelId = useId();
  const buttonId = useId();

  return (
    <div
      className={cn(
        'overflow-hidden rounded-panel border border-border bg-surface transition-colors',
        isOpen && 'border-primary/25',
        className,
      )}
    >
      <h3>
        <button
          type="button"
          id={buttonId}
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={() => setOpen((open) => !open)}
          className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition hover:bg-white/[0.03] sm:px-5"
        >
          <span className="min-w-0 flex-1">
            <span className="block text-[14px] font-bold tracking-[-0.3px] text-content">
              {title}
            </span>
            {description ? (
              <span className="mt-0.5 block text-[11px] leading-snug text-content-muted">
                {description}
              </span>
            ) : null}
          </span>

          {badge ? (
            <span className="shrink-0 rounded-pill bg-white/5 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.08em] text-content-muted">
              {badge}
            </span>
          ) : null}

          {/* A chevron drawn in CSS: no icon dependency for one caret. */}
          <span
            aria-hidden="true"
            className={cn(
              'grid size-6 shrink-0 place-items-center rounded-full border border-border text-[10px] transition-transform duration-200',
              isOpen ? 'rotate-180 border-primary/40 text-primary' : 'text-content-muted',
            )}
          >
            ▾
          </span>
        </button>
      </h3>

      {isOpen ? (
        <div
          id={panelId}
          role="region"
          aria-labelledby={buttonId}
          className="border-t border-border px-4 pb-1 pt-1 sm:px-5"
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

/** Vertical stack of accordion items. */
export function Accordion({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex flex-col gap-2.5', className)}>{children}</div>;
}
